/** Command Centre (ADR-0112): agents at work, live or replayed, and the Galaxy of all agents. */
export default {
  en: {
    toolNames: { datatables: "data tables", cases: "cases", notify: "notifications", documents: "documents", email: "e-mail", browser: "the browser", retrieval: "knowledge", knowledge: "knowledge", codeexec: "code runner", filesystem: "files", objectstore: "file storage", rest: "a connected system", soap: "a connected system", postgres: "a database", oracle: "a database", sqlserver: "a database", directory: "the staff directory", messagebus: "messages" },
    title: "Command Centre",
    subtitle: "Watch your agents work on a real request, live or replayed, and see all your agents at once.",
    talkTo: "Talk to",
    view: { label: "View", run: "One request", galaxy: "Galaxy" },
    briefingButton: "Briefing",
    voice: { on: "Voice on", off: "Voice off", unavailable: "Voice is not turned on for this agent. A channel administrator can turn it on in Channels › Voice." },
    theme: { follow: "Follow console theme" },
    fullscreen: { enter: "Full screen", exit: "Leave full screen" },
    kind: { supervisor: "Supervisor team", handoff: "Hand-off team", flow: "Fixed flow", single: "Single agent", member: "Team inside a team" },
    panels: { conversation: "Conversation", newConversation: "New conversation", happened: "What happened", feed: "Live across the organisation", feedSub: "last 10 minutes", agents: "Your agents", seconds: "{{value}} s" },
    empty: {
      conversation: "Ask the agent something below. Its team or steps light up on the stage as the request goes through them.",
      happened: "Every step of the request appears here as it happens.",
      feed: "No requests in the last 10 minutes.",
      agents: "No agents are released to you here yet.",
    },
    you: "You",
    agentSays: "Agent",
    ask: { label: "Your question", placeholder: "Ask {{agent}} something…", send: "Ask", stop: "Stop" },
    howto: {
      run: "Type a question and press Ask: it is a real request, with the same rules, approvals and records as in the Assistant. Members with a dashed ring are teams themselves: open one to look inside.",
      galaxy: "Every star is an agent. Lines show who calls whom. Open a team or a flow to watch one request.",
    },
    metrics: { model: "Model", steps: "Steps", checks: "Safety checks", online: "Online model", local: "On our own servers", none: "—" },
    state: { working: "working…", askingTeam: "asking its team…", runsAgent: "runs {{agent}}", handedOver: "handed over" },
    caption: {
      step: "Step {{n}}: {{step}}…",
      subStep: "Step {{n}} hands the work to {{agent}}…",
      asking: "Asking {{member}}…",
      teamAsks: "{{team}} is asking its own team…",
      helping: "{{member}} is taking this one…",
      handsOver: "{{from}} hands over to {{member}}…",
      tool: "Using {{tool}}…",
    },
    timeline: {
      step: "Step {{n}}: {{step}}",
      subStep: "Step {{n}} runs the agent {{agent}}",
      teamAsks: "{{team}} asked {{member}}",
      handsOver: "{{from}} handed over to {{member}}",
      memberRefused: "{{member}} was not allowed to help",
      memberFailed: "{{member}} could not finish",
      tool: "Used {{tool}}",
      safetyMany: "Safety checks passed ({{count}})",
      toolFailed: "{{tool}} did not work",
      delivered: "Answer delivered. Every step is in the audit trail.",
    },
    checked: { question: "The question was checked", answer: "The answer was checked" },
    approval: {
      title: "A person must approve",
      input: "A person must answer",
      body: "The agent waits here until a person with the right role decides. Nothing is approved from this screen.",
      open: "Open it in the reviewer inbox",
      who: "Who can decide: {{roles}}",
    },
    crumb: { back: "Back to {{name}}" },
    zoom: "Look inside {{name}}",
    moons: "{{name}} asks its own team: {{members}}",
    track: { more: "and {{count}} more steps", runsAgent: "runs an agent", steps: "Steps of {{name}}" },
    members: "Members of {{name}}",
    hidden: {
      structure: "How this agent is put together is not visible to you. Its requests still show here as they happen.",
      member: "Not visible to you",
    },
    replay: {
      label: "Replay of a stored request",
      play: "Play",
      pause: "Pause",
      speed: "Speed",
      again: "Watch again",
      open: "Open in Command Centre",
      openShort: "Watch",
      notFound: "This request could not be opened. It may belong to someone else, or it is older than what is kept.",
    },
    boot: {
      title: "Agent Command Centre",
      hint: "The assistant can say its briefing and answers aloud. Choose how to start.",
      voice: "Start with voice",
      silent: "Start silently",
      agents: "Agents you can talk to: {{count}}",
      approvals: "Approvals waiting for you: {{count}}",
      status: { ok: "Platform: all parts working", degraded: "Platform: some parts need attention", down: "Platform: not working", unknown: "Platform status unknown" },
    },
    briefing: {
      title: "Briefing",
      items: {
        approvals: "Approvals waiting for you",
        requests: "Requests today",
        waiting: "Requests waiting for a person",
        safety: "Messages stopped by the safety checks today",
        alerts: "Agents with an alert: {{names}}",
      },
      hidden: "Not visible to you: {{parts}}.",
      parts: { approvals: "approvals (you have no reviewer inbox)", activity: "today's requests", health: "agent health" },
      say: {
        hello: "Here is your briefing.",
        approvals: "Approvals waiting for you: {{count}}.",
        requests: "Requests today: {{count}}.",
        waiting: "Requests waiting for a person: {{count}}.",
        safety: "Messages stopped by the safety checks today: {{count}}.",
        alerts: "Agents with an alert: {{names}}.",
      },
    },
    galaxy: {
      label: "All your agents",
      star: "{{name}}, {{kind}}",
      member: "{{name}} works inside a team: you cannot talk to it directly",
      kind: { team: "team", flow: "fixed flow", single: "agent" },
      legend: { teams: "Teams", flows: "Fixed flows", singles: "Single agents and members", live: "Running now" },
      feed: "{{agent}}: {{status}}",
      ago: "{{time}}",
    },
    error: { load: "This could not be loaded." },
  },
  ar: {
    toolNames: { datatables: "جداول البيانات", cases: "الحالات", notify: "الإشعارات", documents: "المستندات", email: "البريد الإلكتروني", browser: "المتصفح", retrieval: "المعرفة", knowledge: "المعرفة", codeexec: "مشغّل الشيفرة", filesystem: "الملفات", objectstore: "تخزين الملفات", rest: "نظام مرتبط", soap: "نظام مرتبط", postgres: "قاعدة بيانات", oracle: "قاعدة بيانات", sqlserver: "قاعدة بيانات", directory: "دليل الموظفين", messagebus: "الرسائل" },
    title: "مركز القيادة",
    subtitle: "شاهد وكلاءك وهم يعملون على طلب حقيقي، مباشرة أو بإعادة التشغيل، واطّلع على جميع وكلائك معًا.",
    talkTo: "تحدث إلى",
    view: { label: "العرض", run: "طلب واحد", galaxy: "المجرّة" },
    briefingButton: "الإيجاز",
    voice: { on: "الصوت مفعّل", off: "الصوت متوقف", unavailable: "الصوت غير مفعّل لهذا الوكيل. يمكن لمسؤول القنوات تفعيله من القنوات › الصوت." },
    theme: { follow: "اتباع مظهر وحدة التحكم" },
    fullscreen: { enter: "ملء الشاشة", exit: "الخروج من ملء الشاشة" },
    kind: { supervisor: "فريق بمشرف", handoff: "فريق بالتسليم", flow: "مسار ثابت", single: "وكيل منفرد", member: "فريق داخل فريق" },
    panels: { conversation: "المحادثة", newConversation: "محادثة جديدة", happened: "ما الذي حدث", feed: "مباشر على مستوى المؤسسة", feedSub: "آخر 10 دقائق", agents: "وكلاؤك", seconds: "{{value}} ث" },
    empty: {
      conversation: "اسأل الوكيل شيئًا في الأسفل. يضيء فريقه أو خطواته على المنصة كلما مرّ الطلب بها.",
      happened: "تظهر هنا كل خطوة من الطلب لحظة حدوثها.",
      feed: "لا طلبات في آخر 10 دقائق.",
      agents: "لا يوجد وكلاء متاحون لك هنا بعد.",
    },
    you: "أنت",
    agentSays: "الوكيل",
    ask: { label: "سؤالك", placeholder: "اسأل {{agent}} شيئًا…", send: "اسأل", stop: "إيقاف" },
    howto: {
      run: "اكتب سؤالًا واضغط اسأل: إنه طلب حقيقي، بالقواعد والموافقات والسجلات نفسها كما في المساعد. الأعضاء المحاطون بحلقة متقطعة فرق بحد ذاتها: افتح أحدهم لترى ما بداخله.",
      galaxy: "كل نجمة وكيل. الخطوط تبيّن من يستدعي من. افتح فريقًا أو مسارًا لمشاهدة طلب واحد.",
    },
    metrics: { model: "النموذج", steps: "الخطوات", checks: "فحوص الأمان", online: "نموذج عبر الإنترنت", local: "على خوادمنا", none: "—" },
    state: { working: "يعمل…", askingTeam: "يسأل فريقه…", runsAgent: "يشغّل {{agent}}", handedOver: "سلّم المحادثة" },
    caption: {
      step: "الخطوة {{n}}: {{step}}…",
      subStep: "الخطوة {{n}} تسلّم العمل إلى {{agent}}…",
      asking: "أسأل {{member}}…",
      teamAsks: "{{team}} يسأل فريقه…",
      helping: "{{member}} يتولى هذا الطلب…",
      handsOver: "{{from}} يسلّم إلى {{member}}…",
      tool: "أستخدم {{tool}}…",
    },
    timeline: {
      step: "الخطوة {{n}}: {{step}}",
      subStep: "الخطوة {{n}} تشغّل الوكيل {{agent}}",
      teamAsks: "سأل {{team}} {{member}}",
      handsOver: "سلّم {{from}} المحادثة إلى {{member}}",
      memberRefused: "لم يُسمح لـ{{member}} بالمساعدة",
      memberFailed: "لم يتمكن {{member}} من الإنهاء",
      tool: "استُخدم {{tool}}",
      safetyMany: "اجتازت فحوص الأمان ({{count}})",
      toolFailed: "لم ينجح {{tool}}",
      delivered: "سُلّمت الإجابة. كل خطوة مسجلة في سجل التدقيق.",
    },
    checked: { question: "فُحص السؤال", answer: "فُحصت الإجابة" },
    approval: {
      title: "يجب أن يوافق شخص",
      input: "يجب أن يجيب شخص",
      body: "ينتظر الوكيل هنا حتى يقرر شخص لديه الدور المناسب. لا تتم أي موافقة من هذه الشاشة.",
      open: "افتحه في صندوق المراجعة",
      who: "من يمكنه القرار: {{roles}}",
    },
    crumb: { back: "العودة إلى {{name}}" },
    zoom: "اطّلع على ما بداخل {{name}}",
    moons: "{{name}} يسأل فريقه: {{members}}",
    track: { more: "و{{count}} خطوات أخرى", runsAgent: "يشغّل وكيلًا", steps: "خطوات {{name}}" },
    members: "أعضاء {{name}}",
    hidden: {
      structure: "طريقة بناء هذا الوكيل غير ظاهرة لك. ومع ذلك تظهر طلباته هنا لحظة حدوثها.",
      member: "غير ظاهر لك",
    },
    replay: {
      label: "إعادة تشغيل طلب محفوظ",
      play: "تشغيل",
      pause: "إيقاف مؤقت",
      speed: "السرعة",
      again: "شاهد مرة أخرى",
      open: "افتحه في مركز القيادة",
      openShort: "مشاهدة",
      notFound: "تعذّر فتح هذا الطلب. قد يكون لشخص آخر، أو أقدم مما يُحتفظ به.",
    },
    boot: {
      title: "مركز قيادة الوكلاء",
      hint: "يمكن للمساعد أن يقرأ الإيجاز والإجابات بصوت عالٍ. اختر طريقة البدء.",
      voice: "ابدأ بالصوت",
      silent: "ابدأ بصمت",
      agents: "الوكلاء الذين يمكنك التحدث إليهم: {{count}}",
      approvals: "الموافقات التي تنتظرك: {{count}}",
      status: { ok: "المنصة: كل الأجزاء تعمل", degraded: "المنصة: بعض الأجزاء تحتاج إلى انتباه", down: "المنصة: لا تعمل", unknown: "حالة المنصة غير معروفة" },
    },
    briefing: {
      title: "الإيجاز",
      items: {
        approvals: "الموافقات التي تنتظرك",
        requests: "طلبات اليوم",
        waiting: "طلبات تنتظر شخصًا",
        safety: "رسائل أوقفتها فحوص الأمان اليوم",
        alerts: "وكلاء عليهم تنبيه: {{names}}",
      },
      hidden: "غير ظاهر لك: {{parts}}.",
      parts: { approvals: "الموافقات (ليس لديك صندوق مراجعة)", activity: "طلبات اليوم", health: "صحة الوكلاء" },
      say: {
        hello: "إليك الإيجاز.",
        approvals: "الموافقات التي تنتظرك: {{count}}.",
        requests: "طلبات اليوم: {{count}}.",
        waiting: "طلبات تنتظر شخصًا: {{count}}.",
        safety: "رسائل أوقفتها فحوص الأمان اليوم: {{count}}.",
        alerts: "وكلاء عليهم تنبيه: {{names}}.",
      },
    },
    galaxy: {
      label: "جميع وكلائك",
      star: "{{name}}، {{kind}}",
      member: "يعمل {{name}} داخل فريق: لا يمكنك التحدث إليه مباشرة",
      kind: { team: "فريق", flow: "مسار ثابت", single: "وكيل" },
      legend: { teams: "الفرق", flows: "المسارات الثابتة", singles: "الوكلاء المنفردون والأعضاء", live: "يعمل الآن" },
      feed: "{{agent}}: {{status}}",
      ago: "{{time}}",
    },
    error: { load: "تعذّر التحميل." },
  },
};
